<template>
    <div>
        <h1 class="text-xl font-bold leading-tight tracking-tight text-gray-900 md:text-2xl text-primary">
            {{ $t('Reset your password') }}
        </h1>
        <form class="grid gap-6 mb-6 md:grid-cols-2" @submit.prevent="submitForm">
            <div class="md:col-span-2">
                <label for="email" class="block mb-2 text-sm font-medium text-gray-900">{{ $t('Your email') }}</label>
                <input v-model="formData.email" type="email" name="email" id="email"
                    class="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
                    placeholder="email" required />
                <small class="text-sm text-red-600 mt-1">{{ msg['email'] }}</small>
            </div>

            <div>
                <label for="password" class="block mb-2 text-sm font-medium text-gray-900">{{ $t('New password') }}</label>
                <div class="relative">
                    <input :type="togglePassword ? 'text' : 'password'" @input="checkPasswordStrength"
                        v-model="formData.password" name="password" id="password" placeholder="••••••••"
                        class="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
                        required />
                    <div class="absolute right-0 bottom-0 top-0 px-3 py-3 cursor-pointer"
                        @click="togglePassword = !togglePassword">
                        <svg :class="{
                            hidden: !togglePassword,
                            block: togglePassword,
                        }" xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 fill-current text-gray-500"
                            viewBox="0 0 24 24">
                            <path
                                d="M12 19c.946 0 1.81-.103 2.598-.281l-1.757-1.757C12.568 16.983 12.291 17 12 17c-5.351 0-7.424-3.846-7.926-5 .204-.47.674-1.381 1.508-2.297L4.184 8.305c-1.538 1.667-2.121 3.346-2.132 3.379-.069.205-.069.428 0 .633C2.073 12.383 4.367 19 12 19zM12 5c-1.837 0-3.346.396-4.604.981L3.707 2.293 2.293 3.707l18 18 1.414-1.414-3.319-3.319c2.614-1.951 3.547-4.615 3.561-4.657.069-.205.069-.428 0-.633C21.927 11.617 19.633 5 12 5zM16.972 15.558l-2.28-2.28C14.882 12.888 15 12.459 15 12c0-1.641-1.359-3-3-3-.459 0-.888.118-1.277.309L8.915 7.501C9.796 7.193 10.814 7 12 7c5.351 0 7.424 3.846 7.926 5C19.624 12.692 18.76 14.342 16.972 15.558z" />
                        </svg>

                        <svg :class="{
                            hidden: togglePassword,
                            block: !togglePassword,
                        }" xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 fill-current text-gray-500"
                            viewBox="0 0 24 24">
                            <path
                                d="M12,9c-1.642,0-3,1.359-3,3c0,1.642,1.358,3,3,3c1.641,0,3-1.358,3-3C15,10.359,13.641,9,12,9z" />
                            <path
                                d="M12,5c-7.633,0-9.927,6.617-9.948,6.684L1.946,12l0.105,0.316C2.073,12.383,4.367,19,12,19s9.927-6.617,9.948-6.684 L22.054,12l-0.105-0.316C21.927,11.617,19.633,5,12,5z M12,17c-5.351,0-7.424-3.846-7.926-5C4.578,10.842,6.652,7,12,7 c5.351,0,7.424,3.846,7.926,5C19.422,13.158,17.348,17,12,17z" />
                        </svg>
                    </div>

                    <div class="flex items-center mt-4 h-3">
                        <div class="w-2/3 flex justify-between h-2">
                            <div :class="{
                                'bg-red-400':
                                    passwordStrengthText == $t('Too weak') ||
                                    passwordStrengthText ==
                                    $t('Could be stronger') ||
                                    passwordStrengthText ==
                                    $t('Strong password'),
                            }" class="h-2 rounded-full mr-1 w-1/3 bg-gray-300"></div>
                            <div :class="{
                                'bg-orange-400':
                                    passwordStrengthText ==
                                    $t('Could be stronger') ||
                                    passwordStrengthText ==
                                    $t('Strong password'),
                            }" class="h-2 rounded-full mr-1 w-1/3 bg-gray-300"></div>
                            <div :class="{
                                'bg-green-400':
                                    passwordStrengthText ==
                                    $t('Strong password'),
                            }" class="h-2 rounded-full w-1/3 bg-gray-300"></div>
                        </div>
                        <div class="text-gray-500 font-medium text-sm ml-3 leading-none">
                            {{ passwordStrengthText }}
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <label for="password_confirmation" class="block mb-2 text-sm font-medium text-gray-900">
                    {{ $t('Confirm new password') }}
                </label>
                <div class="relative">
                    <input :type="togglePassword ? 'text' : 'password'" v-model="formData.password_confirmation"
                        name="password_confirmation" id="password_confirmation" placeholder="••••••••"
                        class="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
                        :required="true" />
                    <div class="absolute right-0 bottom-0 top-0 px-3 py-3 cursor-pointer"
                        @click="togglePassword = !togglePassword">
                        <svg :class="{
                            hidden: !togglePassword,
                            block: togglePassword,
                        }" xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 fill-current text-gray-500"
                            viewBox="0 0 24 24">
                            <path
                                d="M12 19c.946 0 1.81-.103 2.598-.281l-1.757-1.757C12.568 16.983 12.291 17 12 17c-5.351 0-7.424-3.846-7.926-5 .204-.47.674-1.381 1.508-2.297L4.184 8.305c-1.538 1.667-2.121 3.346-2.132 3.379-.069.205-.069.428 0 .633C2.073 12.383 4.367 19 12 19zM12 5c-1.837 0-3.346.396-4.604.981L3.707 2.293 2.293 3.707l18 18 1.414-1.414-3.319-3.319c2.614-1.951 3.547-4.615 3.561-4.657.069-.205.069-.428 0-.633C21.927 11.617 19.633 5 12 5zM16.972 15.558l-2.28-2.28C14.882 12.888 15 12.459 15 12c0-1.641-1.359-3-3-3-.459 0-.888.118-1.277.309L8.915 7.501C9.796 7.193 10.814 7 12 7c5.351 0 7.424 3.846 7.926 5C19.624 12.692 18.76 14.342 16.972 15.558z" />
                        </svg>
                        <svg :class="{
                            hidden: togglePassword,
                            block: !togglePassword,
                        }" xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 fill-current text-gray-500"
                            viewBox="0 0 24 24">
                            <path
                                d="M12,9c-1.642,0-3,1.359-3,3c0,1.642,1.358,3,3,3c1.641,0,3-1.358,3-3C15,10.359,13.641,9,12,9z" />
                            <path
                                d="M12,5c-7.633,0-9.927,6.617-9.948,6.684L1.946,12l0.105,0.316C2.073,12.383,4.367,19,12,19s9.927-6.617,9.948-6.684 L22.054,12l-0.105-0.316C21.927,11.617,19.633,5,12,5z M12,17c-5.351,0-7.424-3.846-7.926-5C4.578,10.842,6.652,7,12,7 c5.351,0,7.424,3.846,7.926,5C19.422,13.158,17.348,17,12,17z" />
                        </svg>
                    </div>
                </div>
                <small class="text-sm text-red-600 mt-1">{{ msg['password'] }}</small>
            </div>

            <div class="md:col-span-2">
                <button type="submit"
                    class="w-full text-white bg-primary hover:bg-primary-700 focus:ring-4 focus:outline-hidden focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-3 text-center"
                    :disabled="loading">
                    <span v-if="loading">{{ $t('Resetting Password') }}...</span>
                    <span v-else>{{ $t('Reset Password') }}</span>
                </button>
            </div>
            <div v-if="msg['errorMessage']" class="md:col-span-2">
                <p class="text-sm text-red-600">{{ msg['errorMessage'] }}</p>
            </div>
            <div v-if="success" class="md:col-span-2">
                <p class="text-sm text-green-600">{{ msg['successMessage'] }}</p>
            </div>
        </form>
    </div>
</template>

<script>
export default {
    props: {
        token: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            formData: {
                email: '',
                password: '',
                password_confirmation: ''
            },
            msg: [],
            togglePassword: false,
            loading: false,
            passwordStrengthText: "",
            success: false,
        };
    },
    watch: {
        "formData.password_confirmation"(val) {
            if (val !== this.formData.password) {
                this.msg['password'] = this.$t('Passwords do not match');
            } else {
                this.msg['password'] = '';
            }
        },
        "formData.password"(val) {
            if (
                this.formData.password_confirmation &&
                val !== this.formData.password_confirmation
            ) {
                this.msg['password'] = this.$t('Passwords do not match');
            } else {
                this.msg['password'] = '';
            }
        },
    },
    methods: {
        async submitForm() {
            this.msg['errorMessage'] = '';
            this.msg['successMessage'] = '';
            if (!this.formData.email || !this.formData.password || !this.formData.password_confirmation) {
                this.msg['errorMessage'] = this.$t('All fields are required');
                return;
            }
            if (this.formData.password !== this.formData.password_confirmation) {
                this.msg['errorMessage'] = this.$t('Passwords do not match');
                return;
            }
            this.loading = true;
            try {
                const response = await fetch(`/password/reset`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-TOKEN": document
                            .querySelector('meta[name="csrf-token"]')
                            ?.getAttribute("content"),
                    },
                    body: JSON.stringify({
                        ...this.formData,
                        token: this.token
                    }),
                });

                if (!response.ok) {
                    let error = {};
                    try {
                        error = await response.json();
                    } catch (e) {
                        error = { message: this.$t("Password reset failed") };
                    }
                    this.msg['errorMessage'] =
                        error.message ||
                        (error.errors &&
                            Object.values(error.errors).flat().join(" ")) ||
                        this.$t("Password reset failed");
                }
                else {
                    this.success = true;
                    this.msg['successMessage'] = this.$t('Your password has been reset successfully. You may now log in');
                    // Optionally redirect to login page after a delay
                    // setTimeout(() => window.location.href = '/login', 2000);
                    window.location.href = '/login';
                }
            } catch (e) {
                this.msg['errorMessage'] = this.$t("Password reset failed");
            } finally {
                this.loading = false;
            }
        },
        checkPasswordStrength() {
            var strongRegex = new RegExp(
                "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})"
            );
            var mediumRegex = new RegExp(
                "^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})"
            );

            let value = this.formData.password;

            if (strongRegex.test(value)) {
                this.passwordStrengthText = this.$t("Strong password");
            } else if (mediumRegex.test(value)) {
                this.passwordStrengthText = this.$t("Could be stronger");
            } else {
                this.passwordStrengthText = this.$t("Too weak");
            }
        },
    },
};
</script>
